import { ref, uploadBytes } from 'firebase/storage';
import { pathForStage, extensionOf, type OriginalBlobMetadata } from '#types';
import { storage } from '../services/startStorage';

interface UploadTempAttachmentParams {
  content: Uint8Array;
  filename: string;
  contentType: string;
  userId: string;
}

interface UploadTempAttachmentResult {
  /** Content-addressed file id — the sha256 hex of the file bytes plus their
   *  extension. Re-uploading identical content yields the same id and thus the same
   *  `fileFolder`, so the parse cache and the import writers collapse a repeated
   *  upload onto the same rows instead of duplicating them (idempotent re-import). */
  fileId: string;
  /** `temp/<userId>/<fileId>` — the per-file folder. */
  fileFolder: string;
  /** Full GCS path of the original blob (`<fileFolder>/original.<ext>`). */
  originalPath: string;
}

export async function uploadTempAttachment({
  content,
  filename,
  contentType,
  userId,
}: UploadTempAttachmentParams): Promise<UploadTempAttachmentResult> {
  const toHex = (digest: ArrayBuffer): string =>
    Array.from(new Uint8Array(digest), (b) =>
      b.toString(16).padStart(2, '0'),
    ).join('');
  const extension = extensionOf(filename);
  const suffix = new TextEncoder().encode(`:${extension}`);
  const seed = new Uint8Array(content.length + suffix.length);
  seed.set(content, 0);
  seed.set(suffix, content.length);
  const fileId = toHex(await crypto.subtle.digest('SHA-256', seed));
  const fileFolder = `temp/${userId}/${fileId}`;
  const originalPath = pathForStage(fileFolder, 'original', { extension });
  const customMetadata: Record<string, string> = {
    displayName: filename,
    uploadedAt: new Date().toISOString(),
  } satisfies OriginalBlobMetadata;
  const storageRef = ref(storage, originalPath);
  await uploadBytes(storageRef, content, { contentType, customMetadata });
  return { fileId, fileFolder, originalPath };
}
